import { Alert } from '@mui/material'
import { useTranslation } from 'react-i18next'

import type { ExerciseRun, ExerciseType } from '@/api/exercises'
import {
  ClozeRenderer,
  DragGapsRenderer,
  DropdownGapsRenderer,
  SentenceBuilderRenderer,
} from './GapRenderers'
import { ChoiceRenderer, PictureRenderer, TrueFalseRenderer } from './ChoiceRenderers'
import { GroupingRenderer, MatchingRenderer, OrderingRenderer } from './OrderRenderers'
import {
  ErrorCorrectionRenderer,
  OpenAnswerRenderer,
  OralAnswerRenderer,
  TranslationRenderer,
} from './TextRenderers'
import { CrosswordRenderer, ListeningRenderer } from './MediaRenderers'
import { DictationRenderer } from './DictationRenderer'
import type {
  ChoiceContent,
  CrosswordContent,
  DictationContent,
  DropdownGapsContent,
  ErrorCorrectionContent,
  GapsContent,
  GroupingContent,
  ListeningContent,
  MatchingContent,
  OpenAnswerContent,
  OralAnswerContent,
  OrderingContent,
  PictureContent,
  SentenceBuilderContent,
  TranslationContent,
  TrueFalseContent,
} from '../contentTypes'

/**
 * Диспетчер рендереров: по типу задания выбирает свой экран решения.
 *
 * Делегатом остался только тест по карточкам — у него своя страница, и
 * повторять её здесь значило бы вести вторую реализацию тех же правил.
 * Диктант решается тут же: отдельной страницы у него больше нет.
 */

type Props = {
  type: ExerciseType
  content: Record<string, unknown>
  answers: Record<string, unknown>
  onChange: (answers: Record<string, unknown>) => void
  readOnly?: boolean
  runId?: number
  /**
   * Сама попытка, если страница её уже загрузила: из неё рендерер берёт
   * серверные поля работы — счётчик прослушиваний и разбор автопроверки.
   * Без неё рендереру пришлось бы вторым запросом тянуть то же самое.
   */
  run?: ExerciseRun | null
}

export function ExerciseRenderer({
  type,
  content,
  answers,
  onChange,
  readOnly,
  runId,
  run,
}: Props) {
  const { t } = useTranslation()
  // Разбор автопроверки идёт всем рендерерам, а не только диктанту: по нему
  // каждый красит свои элементы. Приходит он только проверенной работе и
  // только тому, кому сервер открыл эталоны, — решающий ученик его не видит.
  const shared = { answers, onChange, readOnly, check: run?.check_details ?? null }

  switch (type) {
    case 'drag_gaps':
      return <DragGapsRenderer content={content as unknown as GapsContent} {...shared} />
    case 'cloze':
      return <ClozeRenderer content={content as unknown as GapsContent} {...shared} />
    case 'dropdown_gaps':
      return <DropdownGapsRenderer content={content as unknown as DropdownGapsContent} {...shared} />
    case 'choice':
      return <ChoiceRenderer content={content as unknown as ChoiceContent} {...shared} />
    case 'true_false':
      return <TrueFalseRenderer content={content as unknown as TrueFalseContent} {...shared} />
    case 'picture':
      return <PictureRenderer content={content as unknown as PictureContent} {...shared} />
    case 'matching':
      return <MatchingRenderer content={content as unknown as MatchingContent} runId={runId} {...shared} />
    case 'sentence_builder':
      return <SentenceBuilderRenderer content={content as unknown as SentenceBuilderContent} {...shared} />
    case 'ordering':
      return <OrderingRenderer content={content as unknown as OrderingContent} {...shared} />
    case 'grouping':
      return <GroupingRenderer content={content as unknown as GroupingContent} {...shared} />
    case 'error_correction':
      return <ErrorCorrectionRenderer content={content as unknown as ErrorCorrectionContent} {...shared} />
    case 'translation':
      return <TranslationRenderer content={content as unknown as TranslationContent} {...shared} />
    case 'open_answer':
      return <OpenAnswerRenderer content={content as unknown as OpenAnswerContent} {...shared} />
    case 'oral_answer':
      return <OralAnswerRenderer content={content as unknown as OralAnswerContent} runId={runId} {...shared} />
    case 'crossword':
      return <CrosswordRenderer content={content as unknown as CrosswordContent} {...shared} />
    case 'listening':
      return <ListeningRenderer content={content as unknown as ListeningContent} {...shared} />
    case 'dictation':
      return <DictationRenderer content={content as unknown as DictationContent} runId={runId} run={run} {...shared} />
    default:
      return <Alert severity="warning">{t('exercises.run.unknownType')}</Alert>
  }
}
